import { FeaturesProps } from '@/blocks/PricingCards/Features';
import {
  NormalizedPricingSectionProps,
  PricingSectionCardProps,
  PricingSectionCardType,
  PricingSectionContentProps,
  PricingSectionCtaButton,
  PricingSectionProps
} from '@/blocks/PricingSection/types';
import { uniqueArray } from '@/utils/objects';

import { SvgIcon } from '@/components/SvgIcon';
import { passButtonIcons } from '@/components/atoms/Button/utils';
import arrowRightSrc from '~/icons/arrow-right.svg';
import iconContactSalesSrc from '~/icons/message-chat-circle.svg';

export function transformStrapiPricingSection(
  data: PricingSectionProps
): NormalizedPricingSectionProps {
  return {
    showMoneyBack: data.showMoneyBack,
    showPaymentOptions: data.showPaymentOptions,

    header: data.header,
    description: data.description,
    explainer: data?.explainer_component?.data?.attributes || null,
    features:
      (data?.features_section?.data?.attributes
        ?.features as FeaturesProps['features']) || null,
    tabs: uniqueArray(
      data.cards
        .map((card) => card?.tab?.data?.attributes?.text)
        .filter(Boolean)
    ) as string[],
    columns:
      data.cards?.filter((card) => card?.type === 'pricing_card') || null,
    headerColumns:
      data.cards?.filter((card) => card?.type === 'left_header_card') || null,
    contactSalesColumns:
      data.cards?.filter(
        (card) =>
          card?.type === 'contact_sales_card' || card?.type === 'need_more_card'
      ) || null,
  };
}

export function getColumnsToRender(
  columns: NormalizedPricingSectionProps['columns'] | null,
  selectedTab: string | null
) {
  return (columns || []).filter((column) => {
    if (!column.tab?.data?.attributes?.text) return true; // display the card at all times if no tab selected
    if (column.tab.data?.attributes?.text === selectedTab) return true;

    return false;
  });
}

export function getPricingSectionContentProps(
  columns: NormalizedPricingSectionProps['columns'],
  headerColumns: NormalizedPricingSectionProps['headerColumns'] | null,
  contactSalesColumns: NormalizedPricingSectionProps['contactSalesColumns'],
  selectedTab: string | null
): PricingSectionContentProps {
  return {
    columns: getColumnsToRender(columns, selectedTab).map((column) => ({
      ...column,
      ctaButton: {
        ...column.ctaButton,
        ...passButtonIcons({
          ...column.ctaButton,
          fallbackIcon: {
            rightSection: <SvgIcon src={arrowRightSrc} className='h-5 w-5' />,
          },
        }),
      },
    })) as NormalizedPricingSectionProps['columns'],

    headerColumn:
      headerColumns?.find(
        (column) =>
          column.type === 'left_header_card' &&
          (!column.tab || column?.tab?.data?.attributes?.text === selectedTab)
      ) ||
      headerColumns?.[0] ||
      null,

    contactSalesColumns: getColumnsToRender(
      contactSalesColumns,
      selectedTab
    ).map((column) => ({
      ...column,
      ctaButton: {
        ...column.ctaButton,
        ...passButtonIcons({
          ...column.ctaButton,
          fallbackIcon: {
            leftSection: (
              <SvgIcon src={iconContactSalesSrc} className='h-5 w-5' />
            ),
          },
        }),
      },
    })) as PricingSectionContentProps['contactSalesColumns'],
  };
}

export function pricingSectionMobileColumnAsCard(
  column: PricingSectionCardProps,
  headerColumn: PricingSectionCardProps | null
) {
  return {
    headerTitle: column.cardHeader,
    badgeOfHonor: column.badgeOfHonor,
    rows: column.rows.map((row, rowIndex) => {
      return {
        primaryText: row.primaryText,
        secondaryText: row.secondaryText,
        label: headerColumn?.rows?.[rowIndex]?.primaryText || '',
      };
    }),
    ctaButton: column.ctaButton,
    rateLimit: column.rateLimit,
    type: column.type,
  };
}

export const getPricingCardTrackEventName = (
  type: PricingSectionCardType
): string =>
  type === 'contact_sales_card'
    ? 'contact_sales_cta_click'
    : `${type}_cta_click`;

export const getPricingCardTrackId = (
  ctaButton: PricingSectionCtaButton | undefined
): string | undefined => ctaButton?.url?.split('planId=')?.[1];

export function getPricingCardTrackExtra(
  title?: string | undefined,
  ctaButton?: PricingSectionCtaButton | undefined
): Record<string, unknown> {
  return {
    ...(title && { title }),
    ...(ctaButton?.url && { url: ctaButton.url }),
    ...(ctaButton?.text && { buttonText: ctaButton.text }),
  };
}
